import { ToastMessageInterface } from "@app/interfaces/ToastMessageInterface";
import { currentUserAtom, lastToastMessageAtom } from "@app/models/AppStates";
import { RoutesEnum } from "@app/models/RoutesEnum";
import { AuthService } from "@app/services/AuthService";
import { AuthUtil } from "@app/utils/AuthUtil";
import { VersionUtil } from "@app/utils/VersionUtil";
import { ServiceWorkerUtil } from "@app/utils/ServiceWorkerUtil";
import { ErrorUtil } from "@app/utils/ErrorUtil";
import { useKeyboardResize } from "@app/hooks/useKeyboardResize";
import MainMenu from "@view/components/mainMenu/MainMenu";
import ProgressSpinnerOverlay from "@view/components/progressSpinnerOverlay/ProgressSpinnerOverlay";
import ToastComponent from "@view/components/toastComponent/ToastComponent";
import ChangelogOverlay from "@view/components/changelogOverlay/ChangelogOverlay";
import DashBoardScreen from "@view/screens/dashBoardScreen/DashBoardScreen";
import LoginScreen from "@view/screens/loginScreen/LoginScreen";
import UserDetailScreen, { UserDetailScreenMode } from "@view/screens/userDetailScreen/UserDetailScreen";
import UserListScreen from "@view/screens/userListScreen/UserListScreen";
import UserTokenUsageScreen from "@view/screens/userTokenUsageScreen/UserTokenUsageScreen";
import OcrScreenHoc from "@view/screens/ocrScreen/OcrScreenHoc";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { matchPath } from "react-router";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAtom } from "jotai";
import { ClaudeService } from "../app/services/ClaudeService";
import { GeminiService } from "../app/services/GeminiService";
import { MistralService } from "../app/services/MistralService";
import { OpenAiService } from "../app/services/OpenAiService";
import { OpenRouterService } from "../app/services/OpenRouterService";
import "./EnhancedChatGptApp.scss";
import ChatScreenHoc from "./screens/chatScreen/ChatScreenHoc";
import CompanyDetailScreen, { CompanyDetailScreenMode } from "./screens/companyDetailScreen/CompanyDetailScreen";
import CompanyListScreen from "./screens/companyListScreen/CompanyListScreen";
import CompanyUsersListScreen from "./screens/companyUsersListScreen/CompanyUsersListScreen";
import CustomEndpointsScreen from "./screens/customEndpointsScreen/CustomEndpointsScreen";
import CustomEndpointDetailScreen, { CustomEndpointDetailScreenMode } from "./screens/customEndpointDetailScreen/CustomEndpointDetailScreen";
import CustomEndpointChatRoute from "./screens/chatScreen/CustomEndpointChatRoute";
import { AiServicesAvailabilityService } from "../app/services/AiServicesAvailabilityService";
import { CustomEndpointService } from "../app/services/CustomEndpointService";
import { CustomEndpointInterface } from "@dashart/dashart-gpt-shared-library";

const AUTH_SESSION_CHECK_INTERVAL = 1000 * 60 * .5; // 30 seconds
const LOCAL_STORAGE_KEY__AFTER_LOGIN_REDIRECT = "afterLoginRedirect";

export interface EnhancedChatGptAppProps {
}

const EnhancedChatGptApp: React.FunctionComponent<EnhancedChatGptAppProps> = (props: EnhancedChatGptAppProps) => {
    const location = useLocation();
    const [initialDelayActive, setInitialDelayActive] = useState(true);
    const [lastToastMessage, setLastToastMessage] = useAtom(lastToastMessageAtom);
    const { t } = useTranslation();

    // Handle mobile keyboard resize using Visual Viewport API
    useKeyboardResize();

    const [loginLoading, setLoginLoading] = useState<boolean>(false);
    const [lastLoginTryFailed, setLastLoginTryFailed] = useState<boolean>(false);
    const [currentUser, setCurrentUser] = useAtom(currentUserAtom);
    const [showChangelog, setShowChangelog] = useState<boolean>(false);
    const [changelogForceShow, setChangelogForceShow] = useState<boolean>(false);

    const [availableChatServices, setAvailableChatServices] = useState<{
        chatGpt: boolean,
        claude: boolean,
        mistral: boolean,
        gemini: boolean,
        openRouter: boolean,
        ocr: boolean,
        customEndpoints: boolean,
    }>({
        chatGpt: false,
        claude: false,
        mistral: false,
        gemini: false,
        openRouter: false,
        ocr: false,
        customEndpoints: false,
    });

    const [customEndpoints, setCustomEndpoints] = useState<CustomEndpointInterface[]>([]);

    /**
     * Validate session helper function
     * Checks if the current session is still valid and refreshes tokens if needed
     * If session is invalid, clears tokens and shows login screen
     */
    const endSessionLocally = () => {
        AuthUtil.clearTokens();
        setCurrentUser(null);
    };

    const validateSession = (isRetry = false) => {
        const refreshTokenAtStart = AuthUtil.getRefreshToken();

        AuthService.checkIfSessionValidAndGetNewRefreshToken().then((response) => {
            setCurrentUser(response);
        }).catch((error) => {
            // Only clear tokens and logout if it's an authentication error (401)
            // Don't logout for network errors, timeouts, or other transient failures
            const isAuthError = error?.statusCode === 401 || error?.status === 401;
            const isNetworkError = ErrorUtil.isNetworkError(error);

            if (isAuthError && !isNetworkError) {
                // With multiple tabs the shared refresh token may have been
                // rotated by another tab while this validation was in flight,
                // so our 401 only proves the token we sent was stale, not that
                // the session is gone. If tokens in storage changed since we
                // started, retry once with the current tokens before logging out.
                const tokensChangedElsewhere = !isRetry
                    && AuthUtil.getRefreshToken() !== refreshTokenAtStart;

                if (tokensChangedElsewhere) {
                    validateSession(true);
                    return;
                }

                // Session is actually invalid (401), clear tokens and show login screen
                endSessionLocally();
            }
            // For network errors or other transient errors, keep user logged in
            // The session might still be valid, we just couldn't reach the server
        });
    };

    /**
     * React immediately when another tab logs out. The storage event only
     * fires in the OTHER tabs of the same origin, so no self-echo is possible.
     * Token rotations need no handling: the request interceptor reads the
     * (rotated) access token fresh from storage with every request.
     */
    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key !== null
                && event.key !== AuthUtil.LS_KEYS.JWT_REFRESH_TOKEN
                && event.key !== AuthUtil.LS_KEYS.JWT_ACCESS_TOKEN) {
                return;
            }

            // A logout in another tab (or a localStorage.clear()) emptied the
            // shared session - show the login screen without hitting the
            // server, the revocation happened over there.
            if (!AuthUtil.getRefreshToken()) {
                setCurrentUser(null);
            }
        };

        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    /**
     * Try to validate session on app start
     */
    useEffect(() => {
        validateSession();
    }, []);

    /**
     * Periodically check session validity and validate on app visibility change
     * This prevents the app from getting stuck when session expires during inactivity
     */
    useEffect(() => {
        // Periodic session check (every 30 seconds)
        const intervalId = setInterval(() => {
            if (currentUser) {
                validateSession();
            }
        }, AUTH_SESSION_CHECK_INTERVAL);

        // Handle visibility change (when PWA/tab becomes visible again)
        const handleVisibilityChange = () => {
            if (!document.hidden && currentUser) {
                // App became visible, validate session
                validateSession();
            }
        };

        // Handle window focus (backup for visibility change)
        const handleFocus = () => {
            if (currentUser) {
                validateSession();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [currentUser]);

    /**
     * Check for available chat services on user change
     */
    useEffect(() => {
        if (currentUser) {
            checkForAvailableChatServices();
        }
    }, [currentUser]);

    /**
     * Check if changelog should be shown on app load
     */
    useEffect(() => {
        const currentVersion = process.env.APP_VERSION || '0.5.0';
        
        // Only check for changelog after user is authenticated and initial delay is done
        if (currentUser && !initialDelayActive) {
            setTimeout(() => {
                if (VersionUtil.shouldShowChangelog(currentVersion)) {
                    setShowChangelog(true);
                    setChangelogForceShow(false); // This is automatic display
                    // Mark as seen immediately when automatically showing
                    VersionUtil.markVersionSeen(currentVersion);
                }
            }, 1000); // Small delay to let the app fully load
        }
    }, [currentUser, initialDelayActive]);

    /**
     * Listen for service worker updates and notify the user when a new version is available.
     *
     * This effect sets up a listener for service worker update events. When a new version
     * of the app is deployed and detected:
     *
     * 1. A toast notification is shown to inform the user that an update is available
     * 2. User can click on the toast to manually trigger the reload
     * 3. The page will reload automatically (handled by ServiceWorkerUtil) to use the new version
     *
     * This only runs in production mode, as service workers are not registered in development.
     *
     * The effect also performs an immediate update check when the component mounts to catch
     * any updates that may have been deployed while the user had the app open.
     *
     * The effect includes cleanup to unregister the callback when dependencies change or
     * the component unmounts, preventing callback accumulation and multiple notifications.
     *
     * @dependencies t - Translation function for i18n
     * @dependencies setLastToastMessage - Jotai atom setter for toast messages
     */
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            /**
             * Handler function called when a service worker update becomes available.
             * Shows a toast notification that users can click to trigger the reload manually.
             */
            const handleUpdateAvailable = () => {
                // Create a clickable toast notification using React element
                const toastContent = (
                    <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            ServiceWorkerUtil.skipWaiting();
                        }}
                    >
                        {t('globals.update_available_detail') || 'A new version is available. Click here to reload.'}
                    </div>
                );

                // Show toast notification to inform user about the update
                setLastToastMessage({
                    severity: 'info',
                    summary: t('globals.update_available') || 'Update Available',
                    detail: toastContent,
                    lifetime: 30000, // Display for 30 seconds
                    sticky: false,
                });
            };

            // Register the update handler to be called when an update is detected
            // The registration returns an unregister function for cleanup
            const unregister = ServiceWorkerUtil.onUpdateAvailable(handleUpdateAvailable);

            // Perform an immediate update check when component mounts
            // This catches updates that may have been deployed while the app was open
            ServiceWorkerUtil.checkForUpdates();

            // Cleanup function to unregister the callback when effect re-runs or component unmounts
            return () => {
                unregister();
            };
        }
    }, [t, setLastToastMessage]);

    /**
     * Handle changelog close - only hide, don't mark as seen unless user chose "don't show again"
     */
    const handleChangelogClose = () => {
        // Just close the overlay, don't mark as seen
        // The ChangelogOverlay component will handle marking as seen if user checked "don't show again"
        setShowChangelog(false);
    };

    /**
     * Check for available chat services
     */
    const checkForAvailableChatServices = () => {
        AiServicesAvailabilityService.getInstance().getAiServicesAvailability().then((response) => {
            setAvailableChatServices({
                chatGpt: response.data.services.openai.available,
                claude: response.data.services.claude.available,
                mistral: response.data.services.mistral.available,
                gemini: response.data.services.gemini.available,
                openRouter: response.data.services.openrouter.available,
                ocr: response.data.services.ocr.available,
                customEndpoints: response.data.services.customEndpoints?.available ?? false,
            });

            if (response.data.services.customEndpoints?.available) {
                CustomEndpointService.getAll().then(setCustomEndpoints).catch((err) => {
                    console.error('Failed to load custom endpoints:', err);
                    setCustomEndpoints([]);
                });
            } else {
                setCustomEndpoints([]);
            }
        }).catch((error) => {
            // Session expired errors are already handled in checkSessionValidity (logout + throw).
            // All other errors are transient — leave availability flags at their defaults.
            console.error('Failed to check available chat services:', error);
        });
    }

    /**
     * Try to login user
     * @param userName
     * @param password
     */
    const login = (userName: string, password: string): void => {
        setLoginLoading(true);
        setLastLoginTryFailed(false);

        AuthService.login(userName, password).then((response) => {
            setCurrentUser(response);
        }).catch(
            (error) => {
                const errorMessage = ErrorUtil.extractErrorMessage(error, undefined, t);

                if(ErrorUtil.isNetworkError(error)) {
                    showToast({
                        severity: "error",
                        summary: t("globals.connection_failed"),
                        detail: errorMessage
                    });
                } else {
                    showToast({
                        severity: "error",
                        summary: t("globals.login_failed"),
                        detail: errorMessage
                    });
                }

                setLastLoginTryFailed(true);
            }
        ).then(() => {
            setLoginLoading(false);
        });
    }

    /**
     * Try to logout user
     */
    const logout = (): void => {
        AuthService.logout().then((response) => {

        }).catch(
            (error) => {
                const errorMessage = ErrorUtil.extractErrorMessage(error, undefined, t);
                showToast({
                    severity: "error",
                    summary: t("globals.login_failed"),
                    detail: errorMessage
                });
                setLastLoginTryFailed(true);
            }).then(() => {
                setCurrentUser(null);
            });
    }

    /**
     * Stores last location to local storage
     * @param path
     */
    const setRedirectPathOnAuthentication = (path: string) => {
        localStorage.setItem(LOCAL_STORAGE_KEY__AFTER_LOGIN_REDIRECT, path);
    }

    /**
     * Loads last location from local storage
     * @param andFlush - if true last location will be deleted
     */
    const getRedirectPathOnAuthentication = (andFlush: boolean = false): string => {
        return localStorage.getItem(LOCAL_STORAGE_KEY__AFTER_LOGIN_REDIRECT);
    }

    const showToast = (toast: ToastMessageInterface): void => {
        setLastToastMessage(toast);
    }

    // delay initial view by 1000s to get store ready and load auth data
    useEffect(() => {
        const timer = setTimeout(() => {
            setInitialDelayActive(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    // Custom function to determine if a route is active
    const isActiveRoute = (path: string): boolean => {
        const match = matchPath(
            {
                path,
                caseSensitive: false, // Optional, `true` == static parts of `path` should match case
                end: true, // Optional, `true` == match to end of location.pathname
            },
            location.pathname
        );
        return match !== null;
    };

    return <div className='EnhancedChatGptApp'>

        {!currentUser ?
            <LoginScreen
                loading={loginLoading}
                loginFailed={lastLoginTryFailed}
                overlay={true}
                login={login}   
                loginFailedText={t('globals.login_failed')}
                headerText={t('components.login_screen.header')}
                loginButtonText={t('globals.actions.login')}
                cancelButtonText={t('globals.actions.cancel')}
                resetButtonText={t('globals.actions.reset')}
                userNameLabelText={t('components.login_screen.field_email')}
                passwordLabelText={t('components.login_screen.field_password')}
                userNameRequiredMessage={t('globals.validationFailedNotice')}
                userNameEmailMessage={t('globals.validationFailedNotice')}
                passwordRequiredMessage={t('globals.validationFailedNotice')}
                passwordMinLengthMessage={t('globals.validationFailedNotice')}
            />
            :
            null
        }

        <MainMenu
            user={currentUser}
            availableServices={availableChatServices}
            customEndpoints={customEndpoints}
            tryLogout={logout}
            onShowChangelog={() => {
                setShowChangelog(true);
                setChangelogForceShow(true); // This is manual display
            }}
        />

        {initialDelayActive ?
            <ProgressSpinnerOverlay visible={true} /> :
            <>
                <div className={'screen-holder-outer'}>
                    <div className={'screen-holder'}>
                        <ToastComponent lastToast={lastToastMessage} />
                        <div className="version-display">
                            v{process.env.APP_VERSION}
                        </div>

                        <Routes>

                            {/*reset password screen*/}
                            <Route path={RoutesEnum.resetPassword}>
                                {/*<ResetPasswordScreenContainer/>*/}
                            </Route>


                            <Route path="/" element={
                                currentUser ? <Navigate to={RoutesEnum.chatWithChatGpt} /> : null
                            } />

                            <Route path={RoutesEnum.dashboard} element={
                                currentUser ? <DashBoardScreen /> : null
                            } />

                            {/*chat screens*/}

                            <Route path={RoutesEnum.chatWithChatGpt} element={
                                currentUser ? <ChatScreenHoc
                                    chatService={OpenAiService.getInstance()}
                                /> : null
                            } />

                            <Route path={RoutesEnum.chatWithClaude} element={
                                currentUser ? <ChatScreenHoc
                                    chatService={ClaudeService.getInstance()}
                                /> : null
                            } />

                            <Route path={RoutesEnum.chatWithMistral} element={
                                currentUser ? <ChatScreenHoc
                                    chatService={MistralService.getInstance()}
                                /> : null
                            } />

                            <Route path={RoutesEnum.chatWithGemini} element={
                                currentUser ? <ChatScreenHoc
                                    chatService={GeminiService.getInstance()}
                                /> : null
                            } />
                            
                            <Route path={RoutesEnum.chatWithOpenRouter} element={
                                currentUser ? <ChatScreenHoc
                                    chatService={OpenRouterService.getInstance()}
                                /> : null
                            } />

                            <Route path={RoutesEnum.chatWithCustomEndpoint} element={
                                currentUser ? <CustomEndpointChatRoute /> : null
                            } />

                            <Route path={RoutesEnum.customEndpointList} element={
                                currentUser ? <CustomEndpointsScreen
                                    currentUser={currentUser}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.customEndpointCreate} element={
                                currentUser ? <CustomEndpointDetailScreen
                                    currentUser={currentUser}
                                    mode={CustomEndpointDetailScreenMode.CREATE}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.customEndpointDetail} element={
                                currentUser ? <CustomEndpointDetailScreen
                                    currentUser={currentUser}
                                    mode={CustomEndpointDetailScreenMode.VIEW}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.customEndpointEdit} element={
                                currentUser ? <CustomEndpointDetailScreen
                                    currentUser={currentUser}
                                    mode={CustomEndpointDetailScreenMode.UPDATE}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.customEndpointDelete} element={
                                currentUser ? <CustomEndpointDetailScreen
                                    currentUser={currentUser}
                                    mode={CustomEndpointDetailScreenMode.DELETE}
                                    showToast={showToast}
                                /> : null
                            } />

                            {/*OCR screen*/}

                            <Route path={RoutesEnum.ocr} element={
                                currentUser ? <OcrScreenHoc
                                    showToast={showToast}
                                /> : null
                            } />

                            {/*user administration screens*/}

                            <Route path={RoutesEnum.userList} element={
                                currentUser ? <UserListScreen
                                    currentUser={currentUser}
                                /> : null
                            } />
                            <Route path={RoutesEnum.userDetail} element={
                                currentUser ? <UserDetailScreen
                                    currentUser={currentUser}
                                    mode={UserDetailScreenMode.VIEW}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.userEdit} element={
                                currentUser ? <UserDetailScreen
                                    currentUser={currentUser}
                                    mode={UserDetailScreenMode.UPDATE}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.userCreate} element={
                                currentUser ? <UserDetailScreen
                                    currentUser={currentUser}
                                    mode={UserDetailScreenMode.CREATE}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.userDelete} element={
                                currentUser ? <UserDetailScreen
                                    currentUser={currentUser}
                                    mode={UserDetailScreenMode.DELETE}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.userTokenUsage} element={
                                currentUser ? <UserTokenUsageScreen
                                    currentUser={currentUser}
                                    showToast={showToast}
                                /> : null
                            } />

                            {/* company administration screens */}
                            <Route path={RoutesEnum.companyList} element={
                                currentUser ? <CompanyListScreen
                                    currentUser={currentUser}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.companyDetail} element={
                                currentUser ? <CompanyDetailScreen
                                    currentUser={currentUser}
                                    mode={CompanyDetailScreenMode.VIEW}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.companyEdit} element={
                                currentUser ? <CompanyDetailScreen
                                    currentUser={currentUser}
                                    mode={CompanyDetailScreenMode.UPDATE}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.companyCreate} element={
                                currentUser ? <CompanyDetailScreen
                                    currentUser={currentUser}
                                    mode={CompanyDetailScreenMode.CREATE}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.companyDelete} element={
                                currentUser ? <CompanyDetailScreen
                                    currentUser={currentUser}
                                    mode={CompanyDetailScreenMode.DELETE}
                                    showToast={showToast}
                                /> : null
                            } />
                            <Route path={RoutesEnum.companyListUsers} element={
                                currentUser ? <CompanyUsersListScreen
                                    currentUser={currentUser}
                                    showToast={showToast}
                                /> : null
                            } />


                            {/*fall back to default screens*/}

                            <Route path={RoutesEnum.dashboard} element={
                                currentUser ? <DashBoardScreen /> : null
                            } />

                            <Route path={RoutesEnum.catchAll} element={
                                <Navigate to={RoutesEnum.chatWithChatGpt} />
                            } />
                        </Routes>
                    </div>
                </div>

                {/* Changelog overlay */}
                <ChangelogOverlay
                    visible={showChangelog}
                    onHide={handleChangelogClose}
                    currentVersion={process.env.APP_VERSION || '0.5.0'}
                    forceShow={changelogForceShow}
                />

            </>
        }
    </div>
}

export default EnhancedChatGptApp;